import { FC, useEffect, useState } from "react";
import {
  Button,
  color,
  InputGroup,
  InputSelect,
  NAME_REGEX,
  NO_REGEX,
} from "@funded-here-interface/common";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "@funded-here-interface/investor-onboarding/src/store";
import {
  countries,
  countryNameToCodeMap,
} from "@funded-here-interface/common/src/constant/country";
import {
  IndustryGroupMap,
  LegalStructuresMerchantAndSPV,
} from "../../constants/dropdownValues";
import { Industry, LegalStructure, IndustryGroup } from "../../constants/enums";
import { toast } from "react-toastify";
import { useStyles } from "../../styles/BizProfileCss";
import useGetOrgDraft from "../../hooks/useGetOrgDraft";
import useSaveOrgDraft from "../../hooks/useSaveOrgDraft";
import { formatDateDraft } from "@funded-here-interface/common/src/Utils/formatter";

// Custom hooks and utilities
import { useIssuingSPVBizDraftSync } from "./hooks/useIssuingSPVBizDraftSync";
import {
  validateSPVBizProfile,
  createEmptyErrors,
  SPVBizProfileErrors,
} from "./utils/spvBizProfileValidation";

const IssuingSPVBizProfile: FC = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();

  // Redux selectors
  const {
    company: comp,
    regNumber: regno,
    dateOfIncorporation: dateOfIncorporationRedux,
    legalStructure: legalStructureRedux,
    legalStructureOthers: legalStructureOthersRedux,
    industryGroup: industryGroupRedux,
    industryDropdownValues: industryDropdownValuesRedux,
    typeOfInstitute,
    industryOthers: industryOthersRedux,
    countryISOCode: countryRedux,
    address: addressRedux,
    postal: postalRedux,
    website: websiteRedux,
    userFirstName: userFirstNameRedux,
    userLastName: userLastNameRedux,
  } = useSelector((state: RootState) => state.disBiz);
  const { userId, token } = useSelector((state: RootState) => state.auth);

  // API hooks
  const getOrgDraftMutation = useGetOrgDraft();
  const saveOrgDraftMutation = useSaveOrgDraft();

  // Custom hooks
  const { syncIssuingSPVBizToRedux } = useIssuingSPVBizDraftSync();

  // Page state
  const [initPage, setInitPage] = useState(true);

  // Local state
  const [companyName, setCompany] = useState(comp || "");
  const [registrationNumber, setRegNumber] = useState(regno || "");
  const [dateOfIncorporation, setDateOfIncorporation] = useState(
    dateOfIncorporationRedux || "",
  );
  const [legalStructure, setLegStr] = useState(legalStructureRedux || "");
  const [isOtherLegalStructure, setIsOtherLegalStructure] = useState(
    legalStructureRedux === LegalStructure.OTHERS,
  );
  const [legalStructureOthers, setLegalStructurOthers] = useState(
    legalStructureOthersRedux || "",
  );
  const [institutionType, setInstitute] = useState(typeOfInstitute || "");
  const [industryGroup, setIndustryGroup] = useState(industryGroupRedux || "");
  const [industryDropdownValues, setIndustryDropdownValues] = useState<
    Industry[]
  >(industryDropdownValuesRedux || []);
  const [industryOthers, setIndustryOthers] = useState(
    industryOthersRedux || "",
  );
  const [country, setCountry] = useState(countryRedux || "");
  const [address, setAddress] = useState(addressRedux || "");
  const [postalcode, setPostalCode] = useState(postalRedux || "");
  const [website, setWebsite] = useState(websiteRedux || "");
  const [firstName, setUserFirstName] = useState(userFirstNameRedux || "");
  const [lastName, setUserLastName] = useState(userLastNameRedux || "");

  // Error state
  const [error, setError] = useState<SPVBizProfileErrors>(createEmptyErrors());

  /**
   * Clear error for specific field
   */
  const clearError = (field: keyof SPVBizProfileErrors) => {
    setError((prev) => ({ ...prev, [field]: "" }));
  };

  /**
   * Handle legal structure change
   */
  const onLegalStructureChange = (value: LegalStructure) => {
    if (value === LegalStructure.OTHERS) {
      setIsOtherLegalStructure(true);
    } else {
      setIsOtherLegalStructure(false);
      setLegalStructurOthers("");
    }

    setLegStr(value);
    clearError("legalStructure");
  };

  /**
   * Handle industry group change
   */
  const onIndustryGroupChange = (value: IndustryGroup) => {
    setIndustryDropdownValues([]);
    setInstitute("");

    const industry: Industry[] = IndustryGroupMap[value] || [];

    setIndustryGroup(value);
    setIndustryDropdownValues([...industry]);
    clearError("industryGroup");
  };

  /**
   * Handle industry change
   */
  const onIndustryChange = (value: Industry) => {
    setInstitute(value);
    clearError("institutionType");
  };

  /**
   * Load draft data from backend
   */
  useEffect(() => {
    const initializeDraft = async () => {
      if (initPage) {
        try {
          const params = { userId: parseInt(userId), token };
          const data = await getOrgDraftMutation.mutateAsync(params);

          if (data) {
            setCompany(data.companyName || "");
            setRegNumber(data.companyRegistrationNo || "");
            setDateOfIncorporation(formatDateDraft(data.companyStartDate));
            setLegStr(data.legalStructure || "");
            setCountry(data.country || "");
            setAddress(data.legalEntityAddress || "");
            setPostalCode(data.postalCode?.toString() || "");
            setWebsite(data.website || "");
            setUserFirstName(data.userFirstName || "");
            setUserLastName(data.userLastName || "");
            setIndustryGroup(data.industryGroup || "");

            if (data.industryGroup) {
              onIndustryGroupChange(data.industryGroup);
            }

            // Ensure industry is a string, not an array
            if (data.industry) {
              const industryValue = Array.isArray(data.industry)
                ? data.industry[0]
                : data.industry;
              setInstitute(industryValue);
            }
          }
        } catch (error) {
          toast.error(`Error fetching draft: ${error}`);
        } finally {
          setInitPage(false);
        }
      }
    };

    initializeDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initPage, userId, token]);

  /**
   * Save draft to backend
   */
  const saveDraft = (showToast: boolean = true) => {
    const countryCode = countryNameToCodeMap.get(country) || "";
    saveOrgDraftMutation.mutateAsync(
      {
        userId: parseInt(userId),
        token,
        userFirstName: firstName,
        userLastName: lastName,
        companyName,
        companyRegistrationNo: registrationNumber,
        companyStartDate: dateOfIncorporation || null,
        legalStructure,
        industryGroup,
        industry: institutionType,
        countryISOCode: countryCode,
        country,
        address,
        legalEntityAddress: address,
        postalCode: postalcode,
        website: website || null,
      },
      {
        onSuccess: () => {
          if (showToast) {
            toast.success("Draft Saved");
          }
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      },
    );
  };

  /**
   * Handle form submission
   */
  const handleSubmit = () => {
    saveDraft(false);

    // Validate using safe validation function (replaces dangerous eval!)
    const validationResult = validateSPVBizProfile({
      companyName,
      registrationNumber,
      dateOfIncorporation,
      legalStructure,
      legalStructureOthers,
      industryGroup,
      institutionType,
      industryOthers,
      country,
      address,
      postalcode,
      website,
      firstName,
      lastName,
      isOtherLegalStructure,
    });

    if (validationResult.isValid) {
      // Sync to Redux
      syncIssuingSPVBizToRedux({
        company: companyName,
        regNumber: registrationNumber,
        dateOfIncorporation,
        legalStructure,
        legalStructureOthers,
        industryGroup,
        industryDropdownValues,
        typeOfInstitute: institutionType,
        industryOthers,
        countryISOCode: countryNameToCodeMap.get(country) || "",
        address,
        postal: postalcode,
        website,
        userFirstName: firstName.trim(),
        userLastName: lastName.trim(),
      });

      navigate("/supplier-bank-details");
    } else {
      setError(validationResult.errors);
    }
  };

  return (
    <div style={{ marginTop: "70px" }}>
      <InputGroup
        isErrored={error.firstName}
        errorMessage
        onChange={(value: string) => {
          clearError("firstName");
          setUserFirstName(value.replace(NAME_REGEX, ""));
        }}
        labelText="First Name"
        id="firstName"
        value={firstName}
      />
      <InputGroup
        isErrored={error.lastName}
        errorMessage
        onChange={(value: string) => {
          clearError("lastName");
          setUserLastName(value.replace(NAME_REGEX, ""));
        }}
        labelText="Last Name"
        id="lastName"
        value={lastName}
      />
      <InputGroup
        isErrored={error.companyName}
        errorMessage
        onChange={(value: string) => {
          clearError("companyName");
          setCompany(value);
        }}
        labelText="Company Name"
        id="company"
        value={companyName}
      />
      <InputGroup
        isErrored={error.registrationNumber}
        errorMessage
        onChange={(value: string) => {
          clearError("registrationNumber");
          setRegNumber(value);
        }}
        labelText="Registration Number"
        id="registrationNumber"
        value={registrationNumber}
      />
      <InputGroup
        isErrored={error.dateOfIncorporation}
        errorMessage
        onChange={(value: string) => {
          clearError("dateOfIncorporation");
          setDateOfIncorporation(value);
        }}
        labelText="Date of Incorporation"
        id="dateOfIncorporation"
        value={dateOfIncorporation}
        type="date"
      />
      <InputSelect
        isErrored={error.legalStructure}
        errorMessage
        onChange={onLegalStructureChange}
        labelText="Legal Structure"
        id="legalStructure"
        value={legalStructure}
        data={LegalStructuresMerchantAndSPV}
      />
      {isOtherLegalStructure && (
        <InputGroup
          isErrored={error.legalStructureOthers}
          errorMessage
          onChange={(value: string) => {
            clearError("legalStructureOthers");
            setLegalStructurOthers(value);
          }}
          labelText="Please Specify Legal Structure"
          id="legalStructureOthers"
          value={legalStructureOthers}
        />
      )}
      <InputSelect
        isErrored={error.industryGroup}
        errorMessage
        onChange={onIndustryGroupChange}
        labelText="Industry Group"
        value={industryGroup}
        id="industryGroup"
        data={Object.values(IndustryGroup).sort((a, b) => a.localeCompare(b))}
      />
      <InputSelect
        isErrored={error.institutionType}
        errorMessage
        onChange={onIndustryChange}
        labelText="Industry"
        value={institutionType}
        id="institutionType"
        data={[...industryDropdownValues].sort((a, b) => a.localeCompare(b))}
      />
      <InputSelect
        isErrored={error.country}
        errorMessage
        onChange={(value: string) => {
          clearError("country");
          setCountry(value);
        }}
        labelText="Country of Incorporation"
        id="country"
        value={country}
        data={countries}
      />

      <InputGroup
        isErrored={error.address}
        errorMessage
        onChange={(value: string) => {
          clearError("address");
          setAddress(value);
        }}
        labelText="Address"
        id="address"
        value={address}
      />
      <InputGroup
        isErrored={error.postalcode}
        errorMessage
        onChange={(value: string) => {
          clearError("postalcode");
          setPostalCode(value.replace(NO_REGEX, ""));
        }}
        labelText="Postal Code"
        id="postalcode"
        value={postalcode}
      />
      <InputGroup
        isErrored={error.website}
        errorMessage
        onChange={(value: string) => {
          clearError("website");
          setWebsite(value);
        }}
        labelText="Company Website / Online Marketplace Link"
        id="website"
        value={website}
      />
      <Button
        onClick={handleSubmit}
        textColor="#FFFF"
        backgroundColor="#338F86"
        children="Next"
        width="100%"
      />
      <div className={classes.buttonWrapper}>
        <Button
          onClick={() => saveDraft(true)}
          textColor={color.WHITE}
          backgroundColor={color.LOANTEXT}
          children="Save Draft"
          width="100%"
        />
      </div>
    </div>
  );
};

export default IssuingSPVBizProfile;
